import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { FaEdit, FaTrash, FaPlus, FaTimes } from 'react-icons/fa';
import { Eye } from 'lucide-react';

// Types the admin can pick (multiple choice + "pick more than one" is saved as multiple_select)
const QUESTION_TYPES = {
  rating: 'Confidence Scale (1–5)',
  short_answer: 'Short text',
  long_answer: 'Long text',
  multiple_choice: 'Multiple choice'
};
const typeLabel = (type) => type === 'multiple_select' ? 'Multiple choice' : (QUESTION_TYPES[type] || type);

const SHOW_IN = { both: 'Entry and Exit', entry: 'Entry only', exit: 'Exit only' };

const CHOICE_TYPES = ['multiple_choice', 'multiple_select'];
// Only types with marks can belong to a competency
const SCORED_TYPES = ['rating', 'multiple_choice'];
const RATING_VALUES = ['1', '2', '3', '4', '5'];

const emptyForm = {
  text: '',
  help: '',
  type: 'rating',
  competency: '',
  showIn: 'both',
  required: true,
  options: [{ text: '', marks: 0 }, { text: '', marks: 0 }],
  multiple: false,
  ratingMarks: [1, 2, 3, 4, 5],
  scaleMinLabel: 'Not at all confident',
  scaleMaxLabel: 'Extremely confident'
};

const emptySurvey = { entryIntro: '', exitIntro: '', exitOpen: false, competencies: [] };
const KIND_LABEL = { entry: 'Entry Survey', exit: 'Exit Survey' };

const inputClass = 'w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-[#1086a4] disabled:opacity-60';
const marksInputClass = 'w-20 shrink-0 px-2 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-[#1086a4]';
const cardClass = 'border border-border rounded-lg p-4 bg-background mb-6';
const STEPS = ['Survey', 'Competencies', 'Questions'];
const secondaryBtn = 'flex items-center gap-2 px-4 py-2 border border-[#1086a4] text-[#1086a4] rounded-lg hover:bg-[#1086a4]/5 transition-colors text-sm font-medium whitespace-nowrap';
const primaryBtn = 'flex items-center gap-2 px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#0d6e87] disabled:opacity-50 transition-colors text-sm font-medium whitespace-nowrap';

const SurveysTab = ({ auth }) => {
  const [programs, setPrograms] = useState([]);
  const [surveys, setSurveys] = useState([]);                  // list page rows
  const [listLoading, setListLoading] = useState(true);
  // Survey pop-up: { kind, programId, step, isNew }
  const [wizard, setWizard] = useState(null);
  const [basics, setBasics] = useState({ program: '', intro: '', exitOpen: false });
  const [survey, setSurvey] = useState(emptySurvey);
  const [questions, setQuestions] = useState([]);
  const [wizardLoading, setWizardLoading] = useState(false);
  const [wizardError, setWizardError] = useState('');
  const [busy, setBusy] = useState(false);
  const [newCompetency, setNewCompetency] = useState('');
  const [quickAdd, setQuickAdd] = useState({});               // { [groupKey]: { text, type } }
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const getAuthConfig = () => {
    const config = { headers: { 'Content-Type': 'application/json' } };
    if (auth.token) config.headers['Authorization'] = 'Bearer ' + auth.token;
    return config;
  };
  const errorOf = (err, fallback) => err.response?.data?.error || fallback;

  const fetchSurveys = async () => {
    try {
      const res = await axios.get('/api/program-surveys', getAuthConfig());
      setSurveys(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    axios.get('/api/programs', getAuthConfig())
      .then(res => setPrograms(res.data))
      .catch(err => console.error(err));
    fetchSurveys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchQuestions = async () => {
    const res = await axios.get(`/api/program-questions?program=${wizard.programId}`, getAuthConfig());
    setQuestions(res.data);
  };

  // ---- Survey pop-up ----
  const resetWizard = () => {
    setWizardError('');
    setNewCompetency('');
    setQuickAdd({});
  };

  // kind is optional: the first step of the pop-up asks Entry or Exit
  const openAdd = (kind = '') => {
    resetWizard();
    setSurvey(emptySurvey);
    setQuestions([]);
    setBasics({ program: '', intro: '', exitOpen: false });
    setWizard({ kind, programId: '', step: 0, isNew: true });
  };

  const openExisting = async (programId, kind) => {
    resetWizard();
    setWizard({ kind, programId, step: 0, isNew: false });
    setWizardLoading(true);
    try {
      const [surveyRes, questionsRes] = await Promise.all([
        axios.get(`/api/program-surveys/${programId}`, getAuthConfig()),
        axios.get(`/api/program-questions?program=${programId}`, getAuthConfig())
      ]);
      setSurvey(surveyRes.data);
      setQuestions(questionsRes.data);
      setBasics({ program: programId, intro: surveyRes.data[`${kind}Intro`] || '', exitOpen: !!surveyRes.data.exitOpen });
    } catch (err) {
      setWizardError(errorOf(err, 'Failed to load survey'));
    } finally {
      setWizardLoading(false);
    }
  };

  const closeWizard = () => {
    setWizard(null);
    fetchSurveys();
  };

  const goToStep = (step) => setWizard({ ...wizard, step });

  // Step 1: create the survey (new) or save intro / open switch (existing)
  const saveBasics = async () => {
    setBusy(true);
    setWizardError('');
    try {
      let programId = wizard.programId;
      if (wizard.isNew) {
        await axios.post(`/api/program-surveys/${basics.program}/${wizard.kind}`, { intro: basics.intro }, getAuthConfig());
        programId = basics.program;
      }
      const payload = { [`${wizard.kind}Intro`]: basics.intro };
      if (wizard.kind === 'exit') payload.exitOpen = basics.exitOpen;
      const [surveyRes, questionsRes] = await Promise.all([
        axios.put(`/api/program-surveys/${programId}`, payload, getAuthConfig()),
        axios.get(`/api/program-questions?program=${programId}`, getAuthConfig())
      ]);
      setSurvey(surveyRes.data);
      setQuestions(questionsRes.data);
      setWizard({ ...wizard, programId, isNew: false, step: 1 });
    } catch (err) {
      setWizardError(errorOf(err, 'Failed to save'));
    } finally {
      setBusy(false);
    }
  };

  // Step 2: competencies are saved right away
  const saveCompetencies = async (competencies) => {
    setBusy(true);
    setWizardError('');
    try {
      const res = await axios.put(`/api/program-surveys/${wizard.programId}`, { competencies }, getAuthConfig());
      setSurvey(res.data);
      await fetchQuestions();
    } catch (err) {
      setWizardError(errorOf(err, 'Failed to save competencies'));
    } finally {
      setBusy(false);
    }
  };

  const addCompetency = (e) => {
    e.preventDefault();
    const name = newCompetency.trim();
    if (!name) return;
    if (survey.competencies.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      setWizardError(`"${name}" already exists`);
      return;
    }
    setNewCompetency('');
    saveCompetencies([...survey.competencies, { name }]);
  };

  const removeCompetency = (competency) => {
    const count = questions.filter(q => q.competency === competency._id).length;
    const message = count > 0
      ? `Remove "${competency.name}"? Its ${count} question(s) will move to "Questions not linked to a competency".`
      : `Remove "${competency.name}"?`;
    if (!window.confirm(message)) return;
    saveCompetencies(survey.competencies.filter(c => c._id !== competency._id));
  };

  // Step 3: quick-add a question by typing it and pressing Enter
  const groupDefaultType = 'rating';
  const quickAddFor = (groupKey) => quickAdd[groupKey] || { text: '', type: groupDefaultType };
  const setQuickAddFor = (groupKey, value) => setQuickAdd({ ...quickAdd, [groupKey]: { ...quickAddFor(groupKey), ...value } });

  const submitQuickAdd = async (e, competencyId) => {
    e.preventDefault();
    const groupKey = competencyId || 'additional';
    const { text, type } = quickAddFor(groupKey);
    if (!text.trim()) return;
    // Additional questions default to this survey only; scored ones always show in both
    const showIn = competencyId ? 'both' : wizard.kind;

    // Multiple choice needs options + marks, so open the full form
    if (type === 'multiple_choice') {
      openNew(competencyId || '', { text: text.trim(), type, showIn });
      setQuickAddFor(groupKey, { text: '' });
      return;
    }
    setBusy(true);
    setWizardError('');
    try {
      const payload = { text: text.trim(), type, program: wizard.programId, competency: competencyId || null, showIn, required: true };
      if (type === 'rating') {
        payload.options = RATING_VALUES.map((value, i) => ({ text: value, marks: emptyForm.ratingMarks[i] }));
        payload.scaleMinLabel = emptyForm.scaleMinLabel;
        payload.scaleMaxLabel = emptyForm.scaleMaxLabel;
      }
      await axios.post('/api/program-questions', payload, getAuthConfig());
      setQuickAddFor(groupKey, { text: '' });
      await fetchQuestions();
    } catch (err) {
      setWizardError(errorOf(err, 'Failed to add question'));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteSurvey = async (row, kind) => {
    const other = kind === 'entry' ? row.exitEnabled : row.entryEnabled;
    const message = other
      ? `Delete the ${KIND_LABEL[kind]} for ${row.program.title}? Questions shown only in this survey will be deleted. Shared questions and competencies stay for the ${KIND_LABEL[kind === 'entry' ? 'exit' : 'entry']}.`
      : `Delete the ${KIND_LABEL[kind]} for ${row.program.title}? All its competencies and questions will be deleted.`;
    if (!window.confirm(message)) return;
    try {
      await axios.delete(`/api/program-surveys/${row.program._id}/${kind}`, getAuthConfig());
      fetchSurveys();
    } catch (err) {
      console.error(err);
    }
  };

  // ---- Questions ----
  // Opens the full question form (prefilled with anything typed in the quick-add row)
  const openNew = (competencyId = '', extra = {}) => {
    setForm({ ...emptyForm, competency: competencyId, ...extra });
    setEditingId(null);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (q) => {
    const marksOf = (o) => o.marks ?? 0;
    setForm({
      ...emptyForm,
      text: q.text,
      help: q.help || '',
      type: q.type === 'multiple_select' ? 'multiple_choice' : q.type,
      multiple: q.type === 'multiple_select',
      competency: q.competency || '',
      showIn: q.showIn || 'both',
      required: q.required !== false,
      options: CHOICE_TYPES.includes(q.type) ? q.options.map(o => ({ text: o.text, marks: marksOf(o) })) : emptyForm.options,
      ratingMarks: q.type === 'rating' ? q.options.map(marksOf) : emptyForm.ratingMarks,
      scaleMinLabel: q.type === 'rating' ? (q.scaleMinLabel || '') : emptyForm.scaleMinLabel,
      scaleMaxLabel: q.type === 'rating' ? (q.scaleMaxLabel || '') : emptyForm.scaleMaxLabel
    });
    setEditingId(q._id);
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
  };

  // Text types can't be scored, so they can't have a competency
  const changeType = (type) =>
    setForm({ ...form, type, competency: SCORED_TYPES.includes(type) ? form.competency : '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        text: form.text,
        help: form.help,
        type: form.type === 'multiple_choice' && form.multiple ? 'multiple_select' : form.type,
        program: wizard.programId,
        competency: form.competency || null,
        showIn: form.competency ? 'both' : form.showIn,
        required: form.required
      };
      if (form.type === 'rating') {
        payload.options = RATING_VALUES.map((value, i) => ({ text: value, marks: form.ratingMarks[i] }));
        payload.scaleMinLabel = form.scaleMinLabel;
        payload.scaleMaxLabel = form.scaleMaxLabel;
      } else if (form.type === 'multiple_choice') {
        payload.options = form.options;
      }

      if (editingId) {
        await axios.put(`/api/program-questions/${editingId}`, payload, getAuthConfig());
      } else {
        await axios.post('/api/program-questions', payload, getAuthConfig());
      }
      closeForm();
      fetchQuestions();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await axios.delete(`/api/program-questions/${id}`, getAuthConfig());
      fetchQuestions();
    } catch (err) {
      console.error(err);
    }
  };

  // Option helpers (choose the best answer / choose all that apply)
  const updateOption = (index, field, value) =>
    setForm({ ...form, options: form.options.map((o, i) => i === index ? { ...o, [field]: value } : o) });
  const addOption = () => setForm({ ...form, options: [...form.options, { text: '', marks: 0 }] });
  const removeOption = (index) => setForm({ ...form, options: form.options.filter((_, i) => i !== index) });

  // Marks helpers for the rating scale
  const updateFixedMarks = (field, index, value) =>
    setForm({ ...form, [field]: form[field].map((m, i) => i === index ? value : m) });

  const renderMarksInput = (value, onChange) => (
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      required
      className={marksInputClass}
      placeholder="Marks"
    />
  );

  const competencyIds = survey.competencies.map(c => c._id);
  // Questions in this survey: shown in both, or only in this survey's kind
  const surveyQuestions = wizard ? questions.filter(q => (q.showIn || 'both') === 'both' || q.showIn === wizard.kind) : [];
  const additionalQuestions = surveyQuestions.filter(q => !q.competency || !competencyIds.includes(q.competency));
  const otherKind = wizard && (wizard.kind === 'entry' ? 'exit' : 'entry');
  const wizardProgramTitle = programs.find(p => p._id === (wizard?.programId || basics.program))?.title || survey.program?.title || '';
  const availablePrograms = wizard?.isNew && wizard.kind ? programs.filter(p => !surveys.some(s => s.program._id === p._id && s[`${wizard.kind}Enabled`])) : [];

  const renderSurveyList = (kind) => {
    const rows = surveys.filter(s => s[`${kind}Enabled`]);
    return (
      <div className="mb-8">
        <h3 className="font-semibold text-foreground mb-3">{KIND_LABEL[kind]}s</h3>
        {rows.length === 0 ? (
          <button
            onClick={() => openAdd(kind)}
            className="w-full text-foreground/50 text-sm border border-dashed border-border rounded-lg p-6 text-center hover:border-[#1086a4] hover:text-[#1086a4] transition-colors"
          >
            <FaPlus size={10} className="inline mr-1" /> Add your first {KIND_LABEL[kind]}
          </button>
        ) : (
          <div className="space-y-2">
            {rows.map(row => (
              <div
                key={row.program._id}
                onClick={() => openExisting(row.program._id, kind)}
                className="flex items-center justify-between gap-3 border border-border rounded-lg p-4 bg-background hover:border-[#1086a4] cursor-pointer transition-colors"
              >
                <div className="min-w-0">
                  <p className="font-medium text-foreground">{row.program.title}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-foreground/60">
                    <span>{row[`${kind}QuestionCount`]} questions</span>
                    <span>·</span>
                    <span>{row.competencyCount} competencies</span>
                    {kind === 'exit' && (
                      <span className={`px-2 py-0.5 rounded-full ${row.exitOpen ? 'bg-green-100 text-green-700' : 'bg-muted text-foreground/60'}`}>
                        {row.exitOpen ? 'Open' : 'Closed'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <a
                    href={`/survey/${row.program._id}/${kind}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="px-2 py-1 text-[#1086a4] hover:text-[#054581] transition-colors"
                    title="Preview as students see it"
                  >
                    <Eye className="w-4 h-4" />
                  </a>
                  <span className="text-[#1086a4] text-sm px-2"><FaEdit size={14} /></span>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteSurvey(row, kind); }}
                    className="px-2 py-1 text-foreground/40 hover:text-red-500 transition-colors"
                    title="Delete survey"
                  >
                    <FaTrash size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderQuestionRow = (q, index) => (
    <div key={q._id} className="flex items-start justify-between gap-3 py-2 border-b border-border last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground"><span className="text-foreground/40 mr-1">{index + 1}.</span>{q.text}</p>
        <div className="flex flex-wrap gap-1 mt-1">
          <span className="px-2 py-0.5 bg-[#1086a4]/10 text-[#1086a4] rounded-full text-xs">{typeLabel(q.type)}</span>
          {q.type === 'multiple_select' && <span className="px-2 py-0.5 bg-muted text-foreground/60 rounded-full text-xs">Pick more than one</span>}
          {q.showIn && q.showIn !== 'both' && <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs">{SHOW_IN[q.showIn]}</span>}
          {q.required === false && <span className="px-2 py-0.5 bg-muted text-foreground/60 rounded-full text-xs">Optional</span>}
        </div>
      </div>
      <div className="flex gap-2 shrink-0 pt-0.5">
        <button onClick={() => openEdit(q)} className="text-foreground/40 hover:text-[#1086a4] transition-colors" title="Edit"><FaEdit size={14} /></button>
        <button onClick={() => handleDelete(q._id)} className="text-foreground/40 hover:text-red-500 transition-colors" title="Delete"><FaTrash size={14} /></button>
      </div>
    </div>
  );

  // One group of questions (a competency, or Additional questions) with its quick-add row
  const renderQuestionGroup = (title, subtitle, groupQuestions, competencyId) => {
    const groupKey = competencyId || 'additional';
    const typeOptions = competencyId ? SCORED_TYPES : Object.keys(QUESTION_TYPES);
    const current = quickAddFor(groupKey);
    return (
      <div key={groupKey} className="border border-border rounded-lg p-4 mb-3 bg-background">
        <div className="mb-2">
          <h4 className="text-sm font-semibold text-foreground">{title}</h4>
          {subtitle && <p className="text-foreground/50 text-xs">{subtitle}</p>}
        </div>
        {groupQuestions.map(renderQuestionRow)}
        <form onSubmit={(e) => submitQuickAdd(e, competencyId)} className="flex flex-col sm:flex-row gap-2 mt-3">
          <input
            type="text"
            value={current.text}
            onChange={(e) => setQuickAddFor(groupKey, { text: e.target.value })}
            className={inputClass}
            placeholder="Type a question and press Enter…"
          />
          <select
            value={current.type}
            onChange={(e) => setQuickAddFor(groupKey, { type: e.target.value })}
            className={`${inputClass} sm:w-48`}
          >
            {typeOptions.map(t => <option key={t} value={t}>{QUESTION_TYPES[t]}</option>)}
          </select>
          <button type="submit" disabled={busy || !current.text.trim()} className={primaryBtn}>
            <FaPlus size={10} /> Add
          </button>
        </form>
        <button
          type="button"
          onClick={() => openNew(competencyId || '', { showIn: competencyId ? 'both' : wizard.kind })}
          className="text-foreground/50 hover:text-[#1086a4] text-xs mt-2"
        >
          More options (help text, marks, multiple choice…)
        </button>
      </div>
    );
  };

  const renderWizardStep = () => {
    if (wizard.step === 0) {
      return (
        <div className="space-y-4">
          {wizard.isNew && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Survey type</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { kind: 'entry', hint: 'Taken at the start of the program. Shows the student their starting point.' },
                  { kind: 'exit', hint: 'Taken at the end. Shows growth compared with the Entry Survey.' }
                ].map(option => (
                  <button
                    key={option.kind}
                    type="button"
                    onClick={() => {
                      setWizard({ ...wizard, kind: option.kind });
                      setBasics({ ...basics, program: '' });
                    }}
                    className={`text-left p-4 rounded-lg border-2 transition-colors ${wizard.kind === option.kind ? 'border-[#1086a4] bg-[#1086a4]/5' : 'border-border hover:border-[#1086a4]/50'}`}
                  >
                    <p className="font-semibold text-foreground">{KIND_LABEL[option.kind]}</p>
                    <p className="text-foreground/60 text-xs mt-1">{option.hint}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
          {wizard.kind && (
          <>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Program</label>
            {wizard.isNew ? (
              <>
                <select
                  value={basics.program}
                  onChange={(e) => setBasics({ ...basics, program: e.target.value })}
                  className={inputClass}
                >
                  <option value="">Select a program</option>
                  {availablePrograms.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
                </select>
                {availablePrograms.length === 0 && (
                  <p className="text-foreground/40 text-xs mt-1">Every program already has an {KIND_LABEL[wizard.kind]}.</p>
                )}
              </>
            ) : (
              <p className="text-sm text-foreground font-medium">{wizardProgramTitle}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Intro <span className="text-foreground/40 font-normal">(optional)</span>
            </label>
            <textarea
              value={basics.intro}
              onChange={(e) => setBasics({ ...basics, intro: e.target.value })}
              rows={4}
              className={inputClass}
              placeholder={wizard.kind === 'entry'
                ? 'e.g. Take about 10 minutes to reflect on where you are today. There are no right or wrong answers.'
                : 'e.g. You made it! Answer the same questions again so we can show you how far you have come.'}
            />
          </div>
          {wizard.kind === 'exit' && (
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={basics.exitOpen}
                onChange={(e) => setBasics({ ...basics, exitOpen: e.target.checked })}
                className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
              />
              Open to students now
            </label>
          )}
          </>
          )}
        </div>
      );
    }

    if (wizard.step === 1) {
      return (
        <div>
          <p className="text-foreground/60 text-sm mb-3">
            Each competency is one point on the student's results spider chart. Competencies are shared with this program's {KIND_LABEL[otherKind]}.
          </p>
          <form onSubmit={addCompetency} className="flex gap-2 mb-4">
            <input
              type="text"
              value={newCompetency}
              onChange={(e) => setNewCompetency(e.target.value)}
              className={inputClass}
              placeholder="e.g. Communication, then press Enter"
              autoFocus
            />
            <button type="submit" disabled={busy || !newCompetency.trim()} className={primaryBtn}>
              <FaPlus size={10} /> Add
            </button>
          </form>
          {survey.competencies.length === 0 ? (
            <p className="text-foreground/40 text-sm text-center py-6 border border-dashed border-border rounded-lg">
              No competencies yet. You can also skip this and only add additional questions.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {survey.competencies.map(c => (
                <span key={c._id} className="flex items-center gap-2 pl-3 pr-2 py-1.5 bg-[#1086a4]/10 text-[#1086a4] rounded-full text-sm">
                  {c.name}
                  <span className="text-[#1086a4]/60 text-xs">{questions.filter(q => q.competency === c._id).length} Qs</span>
                  <button type="button" onClick={() => removeCompetency(c)} className="hover:text-red-500" title="Remove">
                    <FaTimes size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div>
        <p className="text-foreground/60 text-sm mb-3">
          Questions under a competency are scored and appear in both surveys. New questions are a Confidence Scale (1–5) by default, with marks 1–5.
        </p>
        {survey.competencies.map(c =>
          renderQuestionGroup(c.name, c.description, surveyQuestions.filter(q => q.competency === c._id), c._id)
        )}
        {renderQuestionGroup(
          'Questions not linked to a competency',
          `Not scored on the spider chart. Added here they show in the ${KIND_LABEL[wizard.kind]} only (change it with edit).`,
          additionalQuestions,
          null
        )}
      </div>
    );
  };

  const canGoNext = wizard && (wizard.step !== 0 || !wizard.isNew || (!!wizard.kind && !!basics.program));
  const handleNext = () => {
    if (wizard.step === 0) return saveBasics();
    goToStep(wizard.step + 1);
  };

  return (
    <div>
      {/* List page */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground">Surveys</h2>
          <p className="text-foreground/50 text-sm">Entry and Exit surveys per program. Students see their results as a spider chart by competency.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => openAdd()} className={primaryBtn}><FaPlus size={12} /> Add Survey</button>
        </div>
      </div>
      {listLoading ? (
        <div className="text-foreground/60 p-4">Loading...</div>
      ) : (
        <>
          {renderSurveyList('entry')}
          {renderSurveyList('exit')}
        </>
      )}

      {/* Survey pop-up (step by step) */}
      {wizard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-card rounded-xl shadow-xl w-full max-w-2xl border border-border flex flex-col max-h-[90vh]">
            {/* Header + stepper */}
            <div className="px-6 pt-5 pb-4 border-b border-border">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {wizard.isNew ? (wizard.kind ? `Add ${KIND_LABEL[wizard.kind]}` : 'Add Survey') : KIND_LABEL[wizard.kind]}
                  </h3>
                  {wizardProgramTitle && !wizard.isNew && <p className="text-foreground/60 text-sm">{wizardProgramTitle}</p>}
                </div>
                <div className="flex items-center gap-3">
                  {!wizard.isNew && (
                    <a
                      href={`/survey/${wizard.programId}/${wizard.kind}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-[#1086a4] hover:text-[#054581] text-sm"
                      title="Open the survey as students see it"
                    >
                      <Eye className="w-4 h-4" /> Preview
                    </a>
                  )}
                  <button onClick={closeWizard} className="text-foreground/40 hover:text-foreground/60" title="Close">
                    <FaTimes size={16} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {STEPS.map((label, i) => {
                  const active = wizard.step === i;
                  const done = wizard.step > i;
                  const clickable = !wizard.isNew && !busy;
                  return (
                    <React.Fragment key={label}>
                      <button
                        type="button"
                        disabled={!clickable}
                        onClick={() => clickable && goToStep(i)}
                        className={`flex items-center gap-2 text-sm ${active ? 'text-[#1086a4] font-semibold' : done ? 'text-foreground' : 'text-foreground/40'} ${clickable ? 'hover:text-[#1086a4]' : 'cursor-default'}`}
                      >
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${active ? 'bg-[#1086a4] text-white' : done ? 'bg-[#1086a4]/20 text-[#1086a4]' : 'bg-muted text-foreground/50'}`}>
                          {i + 1}
                        </span>
                        {label}
                      </button>
                      {i < STEPS.length - 1 && <span className="flex-1 h-px bg-border" />}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 overflow-y-auto flex-1">
              {wizardLoading ? <div className="text-foreground/60">Loading...</div> : renderWizardStep()}
              {wizardError && <p className="text-red-500 text-sm mt-3">{wizardError}</p>}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-border flex items-center justify-between">
              <span className="text-foreground/40 text-xs">{wizard.isNew ? '' : 'Changes are saved as you go'}</span>
              <div className="flex gap-2">
                {wizard.step > 0 && (
                  <button type="button" onClick={() => goToStep(wizard.step - 1)} className={secondaryBtn}>Back</button>
                )}
                {wizard.step < STEPS.length - 1 ? (
                  <button type="button" onClick={handleNext} disabled={busy || !canGoNext} className={primaryBtn}>
                    {busy ? 'Saving...' : wizard.step === 0 && wizard.isNew ? 'Create & continue' : 'Next'}
                  </button>
                ) : (
                  <button type="button" onClick={closeWizard} className={primaryBtn}>Finish</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Question Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-card rounded-lg shadow-lg w-full max-w-lg p-6 border border-border max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-foreground">
                {editingId ? 'Edit Question' : 'Add Question'}
              </h3>
              <button onClick={closeForm} className="text-foreground/40 hover:text-foreground/60">
                <FaTimes size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Question</label>
                <textarea
                  value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  required
                  rows={2}
                  className={inputClass}
                  placeholder="Enter the question..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Help text <span className="text-foreground/40 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={form.help}
                  onChange={(e) => setForm({ ...form, help: e.target.value })}
                  className={inputClass}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Question type</label>
                  <select value={form.type} onChange={(e) => changeType(e.target.value)} className={inputClass}>
                    {Object.entries(QUESTION_TYPES).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Competency</label>
                  <select
                    value={form.competency}
                    onChange={(e) => setForm({ ...form, competency: e.target.value })}
                    disabled={!SCORED_TYPES.includes(form.type)}
                    className={inputClass}
                  >
                    <option value="">None (additional question)</option>
                    {survey.competencies.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Show in</label>
                <select
                  value={form.competency ? 'both' : form.showIn}
                  onChange={(e) => setForm({ ...form, showIn: e.target.value })}
                  disabled={!!form.competency}
                  className={inputClass}
                >
                  {Object.entries(SHOW_IN).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                {form.competency && (
                  <p className="text-foreground/40 text-xs mt-1">Scored questions always appear in both surveys so Entry and Exit can be compared.</p>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                <input
                  type="checkbox"
                  checked={form.required}
                  onChange={(e) => setForm({ ...form, required: e.target.checked })}
                  className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
                />
                Required
              </label>

              {/* Confidence scale 1–5: marks per value + optional end labels */}
              {form.type === 'rating' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Marks for each answer <span className="text-foreground/40 font-normal">(1 = lowest, 5 = highest)</span>
                  </label>
                  {RATING_VALUES.map((value, i) => (
                    <div key={value} className="flex items-center gap-2 mb-2">
                      <span className="w-8 text-sm font-medium text-foreground text-center">{value}</span>
                      {renderMarksInput(form.ratingMarks[i], (v) => updateFixedMarks('ratingMarks', i, v))}
                      <span className="text-foreground/40 text-xs">marks</span>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-3">
                    <input
                      type="text"
                      value={form.scaleMinLabel}
                      onChange={(e) => setForm({ ...form, scaleMinLabel: e.target.value })}
                      className={inputClass}
                      placeholder="Label for 1 (optional)"
                    />
                    <input
                      type="text"
                      value={form.scaleMaxLabel}
                      onChange={(e) => setForm({ ...form, scaleMaxLabel: e.target.value })}
                      className={inputClass}
                      placeholder="Label for 5 (optional)"
                    />
                  </div>
                </div>
              )}

              {/* Multiple choice: options with marks */}
              {form.type === 'multiple_choice' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Options and marks</label>
                  {form.options.map((option, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        value={option.text}
                        onChange={(e) => updateOption(i, 'text', e.target.value)}
                        className={inputClass}
                        placeholder={`Option ${i + 1}`}
                      />
                      {renderMarksInput(option.marks, (v) => updateOption(i, 'marks', v))}
                      {form.options.length > 2 && (
                        <button type="button" onClick={() => removeOption(i)} className="text-red-400 hover:text-red-600 px-1">
                          <FaTimes size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addOption} className="text-[#1086a4] text-sm hover:underline flex items-center gap-1">
                    <FaPlus size={10} /> Add option
                  </button>
                  <label className="flex items-center gap-2 text-sm text-foreground mt-3">
                    <input
                      type="checkbox"
                      checked={form.multiple}
                      onChange={(e) => setForm({ ...form, multiple: e.target.checked })}
                      className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
                    />
                    Students can pick more than one
                    {form.multiple && <span className="text-foreground/40 text-xs">(marks of all picked options are added up)</span>}
                  </label>
                </div>
              )}

              {(form.type === 'short_answer' || form.type === 'long_answer') && (
                <p className="text-foreground/50 text-sm">
                  Students type a {form.type === 'long_answer' ? 'longer' : 'short'} free-text answer. It is not scored.
                </p>
              )}

              {formError && <p className="text-red-500 text-sm">{formError}</p>}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 border border-[#1086a4] rounded-lg text-[#1086a4] font-medium hover:bg-[#f9fafb] transition-colors text-sm"
                >
                  Cancel
                </button>
                <button type="submit" disabled={saving} className={primaryBtn}>
                  {saving ? 'Saving...' : editingId ? 'Save changes' : 'Add question'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({ auth: state.auth });
export default connect(mapStateToProps)(SurveysTab);
